import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import { AngleLeft } from '@rushable/icons'
import { AdminLayout } from 'components/Layout'
import Button from 'components/Button'
import PageLoading from 'components/PageLoading'
import { IOptions } from 'components/Select/help'
import { TRowProp } from 'components/Table'
import useDebounce from 'hooks/useDebounce'
import AiCallsDailySummaryTable from './components/AiCallsDailySummaryTable'
import AiCallDrawer from './components/AiCallDrawer'
import AiCallsFilter from './components/AiCallsFilter'
import AiCallsTable from './components/AiCallsTable'
import {
  getAiCallAssignees,
  getAiCallListAssignees,
  getAiCalls,
  resetFilters,
  setData,
  setFilters,
  setMode,
  setOrder,
  setPage,
  setPageSize,
  setTotal,
} from 'redux/aiCalls'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import {
  getPersonAvatar,
  getPersonDisplayName,
  TViewMode,
} from 'pages/AiCalls/helpers/constant'

const AI_CALLS_VIEW_MODE_KEY = 'ai_calls_view_mode'

const cleanParams = (params: Record<string, any>) => {
  return Object.keys(params).reduce((acc: Record<string, any>, key) => {
    if (params[key] !== '' && params[key] !== undefined && params[key] !== null) {
      acc[key] = params[key]
    }
    return acc
  }, {})
}

const getSavedViewMode = (): TViewMode => {
  const savedMode = localStorage.getItem(AI_CALLS_VIEW_MODE_KEY)
  return savedMode === 'list' || savedMode === 'daily' ? savedMode : 'daily'
}

export default function AiCallsPage() {
  const dispatch = useAppDispatch()
  const { filters, mode, sortData, page, pageSize } = useAppSelector(
    state => state.aiCalls,
  )
  const [loading, setLoading] = useState(false)
  const [showDrawer, setShowDrawer] = useState(false)
  const [curItem, setCurItem] = useState<Record<string, any>>({})
  const [filterAssigneeOptions, setFilterAssigneeOptions] = useState<IOptions[]>([])
  const [editAssigneeOptions, setEditAssigneeOptions] = useState<IOptions[]>([])
  const isDailyDrillDown = mode === 'daily' && !!filters.date

  const initParams = useMemo(
    () => ({
      mode: isDailyDrillDown ? 'list' : mode,
      ...filters,
      assignee: filters.assignee_crm_user_id,
      assignee_crm_user_id: undefined,
      sort_by: sortData.sort_by,
      sort: sortData.sort,
      page,
      per_page: pageSize,
    }),
    [filters, isDailyDrillDown, mode, page, pageSize, sortData],
  )

  const getTable = async (params?: any) => {
    setLoading(true)
    try {
      const res = await getAiCalls(
        cleanParams({
          ...initParams,
          ...params,
        }),
      )
      dispatch(setTotal(res.total))
      dispatch(setPageSize(res.per_page))
      dispatch(setData(res.data))
    } catch (e: any) {
      if (e && e.message) {
        toast.error(e.message)
      }
    }
    setLoading(false)
  }

  const handleFormChange = (key: string, value: any) => {
    const params: Record<string, any> = {
      ...initParams,
    }

    if (key === 'page') {
      dispatch(setPage(value))
      params.page = value
    } else if (key === 'mode') {
      localStorage.setItem(AI_CALLS_VIEW_MODE_KEY, value)
      dispatch(setMode(value))
      dispatch(
        setOrder({
          sort_by: '',
          sort: '',
        }),
      )
      params.sort_by = ''
      params.sort = ''
      dispatch(setPage(1))
      dispatch(
        setFilters({
          date: '',
          keyword: value === 'daily' ? '' : filters.keyword,
          status: value === 'daily' ? '' : filters.status,
          assignee_crm_user_id: value === 'daily' ? '' : filters.assignee_crm_user_id,
        }),
      )
      params.mode = value
      params.date = ''
      if (value === 'daily') {
        params.keyword = ''
        params.status = ''
        params.assignee_crm_user_id = ''
        params.assignee = ''
      }
      params.page = 1
    } else if (key === 'sort') {
      dispatch(
        setOrder({
          sort_by: value.value ? value.type : '',
          sort: value.value,
        }),
      )
      dispatch(setPage(1))
      params.sort_by = value.value ? value.type : ''
      params.sort = value.value
      params.page = 1
    } else if (key === 'date_range') {
      const shouldKeepDrillDate = mode === 'daily' && !!filters.date
      dispatch(
        setFilters({
          from: value.from,
          to: value.to,
          date: shouldKeepDrillDate ? filters.date : '',
        }),
      )
      dispatch(setPage(1))
      params.from = value.from
      params.to = value.to
      params.date = shouldKeepDrillDate ? filters.date : ''
      params.page = 1
    } else {
      const shouldKeepDrillDate = mode === 'daily' && !!filters.date
      dispatch(
        setFilters({
          [key]: value,
          date:
            key === 'keyword' || key === 'status' || key === 'assignee_crm_user_id'
              ? shouldKeepDrillDate
                ? filters.date
                : ''
              : params.date,
        }),
      )
      dispatch(setPage(1))
      params[key] = value
      if (key === 'keyword' || key === 'status' || key === 'assignee_crm_user_id') {
        params.date = shouldKeepDrillDate ? filters.date : ''
      }
      params.page = 1
    }

    if (key === 'assignee_crm_user_id') {
      params.assignee = value
      params.assignee_crm_user_id = undefined
    }

    if (key === 'keyword') {
      if (!value || String(value).length > 2) {
        handleKeyword(params)
      }
    } else {
      getTable(params)
    }
  }

  const handleKeyword = useDebounce((params: Record<string, any>) => {
    getTable(params)
  }, 500)

  const handleReset = () => {
    if (isDailyDrillDown) {
      dispatch(
        setFilters({
          status: '',
          keyword: '',
          assignee_crm_user_id: '',
          from: '',
          to: '',
          date: filters.date,
        }),
      )
      dispatch(setPage(1))
      getTable({
        mode: 'list',
        status: '',
        keyword: '',
        assignee_crm_user_id: '',
        assignee: '',
        from: '',
        to: '',
        date: filters.date,
        page: 1,
      })
      return
    }

    dispatch(resetFilters())
    dispatch(setPage(1))
    getTable({
      mode,
      status: '',
      keyword: '',
      assignee_crm_user_id: '',
      assignee: '',
      from: '',
      to: '',
      date: '',
      page: 1,
    })
  }

  const clickedRow = (row: TRowProp) => {
    setCurItem(row)
    setShowDrawer(true)
  }

  const clickedDailyRow = (row: TRowProp) => {
    const date = row?.date || ''
    dispatch(setPage(1))
    dispatch(
      setOrder({
        sort_by: '',
        sort: '',
      }),
    )
    dispatch(
      setFilters({
        date,
        from: '',
        to: '',
      }),
    )
    getTable({
      mode: 'list',
      date,
      from: '',
      to: '',
      sort_by: '',
      sort: '',
      page: 1,
    })
  }

  const handleBackToSummary = () => {
    dispatch(setPage(1))
    dispatch(
      setOrder({
        sort_by: '',
        sort: '',
      }),
    )
    dispatch(
      setFilters({
        date: '',
      }),
    )
    getTable({
      mode: 'daily',
      date: '',
      sort_by: '',
      sort: '',
      page: 1,
    })
  }

  const buildAssigneeOptions = (assignees: any[]) => {
    return [
      {
        label: 'Any assignee',
        value: '',
      },
    ].concat(
      (assignees || []).map((item: any) => ({
        label: getPersonDisplayName(item),
        value: String(item.id),
        img_url: getPersonAvatar(item),
      })),
    )
  }

  const loadFilterAssignees = async () => {
    try {
      const res = await getAiCallListAssignees()
      setFilterAssigneeOptions(buildAssigneeOptions(res || []))
    } catch (e: any) {
      if (e && e.message) {
        toast.error(e.message)
      }
    }
  }

  const loadEditAssignees = async () => {
    try {
      const res = await getAiCallAssignees()
      setEditAssigneeOptions(buildAssigneeOptions(res || []))
    } catch (e: any) {
      if (e && e.message) {
        toast.error(e.message)
      }
    }
  }
  useEffect(() => {
    const savedMode = getSavedViewMode()
    dispatch(setMode(savedMode))
    dispatch(resetFilters())
    dispatch(setPage(1))
    dispatch(
      setOrder({
        sort_by: '',
        sort: '',
      }),
    )
    getTable({
      mode: savedMode,
      status: '',
      keyword: '',
      assignee_crm_user_id: '',
      assignee: '',
      from: '',
      to: '',
      date: '',
      sort_by: '',
      sort: '',
      page: 1,
    })
    loadFilterAssignees()
    loadEditAssignees()
  }, [])

  return (
    <AdminLayout>
      <div
        style={{ maxHeight: loading ? 'calc(100vh - 80px)' : 'none' }}
        className={`relative ${loading && 'overflow-y-hidden'}`}
      >
        {loading && (
          <div className='bg-dark-900 w-full h-full absolute z-40'>
            <PageLoading />
          </div>
        )}

        <div className='px-10 py-8'>
          {isDailyDrillDown && (
            <div className='relative mb-5 flex h-10 items-center'>
              <Button
                theme='text'
                color='secondary'
                iconBefore={<AngleLeft size={12} />}
                onClick={handleBackToSummary}
              >
                GO BACK
              </Button>
              <div className='absolute left-1/2 -translate-x-1/2 text-xl font-bold text-light-700'>
                {moment(filters.date).format('YYYY-MM-DD')}
              </div>
            </div>
          )}
          <AiCallsFilter
            filters={filters}
            mode={mode}
            assigneeOptions={filterAssigneeOptions}
            handleFormChange={handleFormChange}
            handleReset={handleReset}
          />
          <div>
            {isDailyDrillDown ? (
              <AiCallsTable
                loading={loading}
                handleFormChange={handleFormChange}
                clickedRow={clickedRow}
              />
            ) : mode === 'daily' ? (
              <AiCallsDailySummaryTable
                loading={loading}
                handleFormChange={handleFormChange}
                clickedRow={clickedDailyRow}
              />
            ) : (
              <AiCallsTable
                loading={loading}
                handleFormChange={handleFormChange}
                clickedRow={clickedRow}
              />
            )}
          </div>
        </div>
        <AiCallDrawer
          open={showDrawer}
          ticket={curItem}
          assigneeOptions={editAssigneeOptions}
          toggle={() => setShowDrawer(!showDrawer)}
          onUpdated={ticket => {
            if (ticket?.id) {
              setCurItem(ticket)
            }
            getTable()
          }}
        />
      </div>
    </AdminLayout>
  )
}
